import { connect } from 'react-redux';

import { createSelector, defaultMemoize } from 'reselect';
import {
  POST_EDITOR_START,
  COMMENT_EDITOR_START,
  INBOX_EDITOR_START,
  MATERIAL_EDITOR_START,
} from '../../../../../core/constants/analytics';
import { nonCancellableFetchAssignments as fetchAssignmentsAPI } from '../../../../../core/assignments/api';
import { accessToken as getAccessToken } from '../../../../../core/selectors/user';
import { analyticsTrackEvent } from '../../../../../core/utils/analytics';
import * as endpoints from '../../../../constants/endpoints';
import { isInstructor } from '../../../../utils/user';
import { bbbEnabled } from '../../../../../core/selectors/institution';
import getAnalyticsHeaders from '../../../../../core/utils/analyticsHeaders';
import {
  isTurnItInEnabled as getIsTurnItInEnabled,
  isBibliuEnabled as getIsBibliuEnabled,
  isTalisEnabled as getIsTalisEnabled,
  isEchoEnabled as getIsEchoEnabled,
  isPlanetEnabled as getIsPlanetEnabled,
  isPanoptoEnabled as getIsPanoptoEnabled,
} from '../../../../../core/selectors/lti';
import { currentClassRoomId } from '../../../../../core/selectors/classroom';
import goToSubmissions from '../../../../actions/navigation/goToSubmissions.action';

import EditorPlus from './EditorPlus';

const objToArr = (obj) => Object.values(obj);
const flatten = (acc, next) => acc.concat(next);

const getAssignmentsArray = (assignmentsBySpace, space, allSpaces) => {
  if (!allSpaces) {
    const assignmentsInGivenSpace = assignmentsBySpace[space];
    return assignmentsInGivenSpace ? objToArr(assignmentsInGivenSpace) : [];
  }
  const assignmentsArray = objToArr(assignmentsBySpace).map((sp) =>
    objToArr(sp)
  );
  return assignmentsArray.reduce(flatten, []);
};

const editorTypeMap = {
  comment: COMMENT_EDITOR_START,
  createpost: POST_EDITOR_START,
  post: POST_EDITOR_START,
  inbox: INBOX_EDITOR_START,
  material: MATERIAL_EDITOR_START,
};

const COMMENT = 'comment';

const editorTypeToAnalyticsEventType = (type) => editorTypeMap[type] || type;

const mergePluginsConfigProps = (props) => {
  const {
    isTurnitinEnabled,
    isBibliuEnabled,
    isTalisEnabled,
    isEchoEnabled,
    isPlanetEnabled,
    isPanoptoEnabled,
    paddings,
    pluginsConfigs,
    user,
    institution,
    spaceName,
    isBBBEnabled,
    classRoomId,
  } = props;

  const turnitinConfig = isTurnitinEnabled
    ? {
        turnitinProvider: {
          resourceEndpoint: endpoints.ASSIGNMENTS(),
          baseUrl: endpoints.LTI_CONSUMER(),
          userId: user.objectId,
          sessionToken: user.sessionToken,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          hideButton: !props.isInstructor,
          role: (cb) => cb(props.isInstructor),
          classRoomId: (cb) => cb(props.classRoomId),
        },
      }
    : {};

  const assignmentsConfig = {
    assignments: {
      goToAulaSubmissions: (assignmentId, provider) => {
        goToSubmissions({
          spaceId: props.classRoomId,
          assignmentId,
          provider,
        })();
      },
      assignmentsEndpoint: endpoints.ASSIGNMENTS(),
      ltiConsumerEndpoint: endpoints.LTI_CONSUMER(),
      user,
      fetchAssignments: ({ assignmentId, page, externalId }) =>
        fetchAssignmentsAPI({
          accessToken: props.accessToken,
          userId: user.objectId,
          sessionToken: user.sessionToken,
          spaceId: props.classRoomId,
          assignmentId,
          page,
          externalId,
        }),
      institution,
      isInstructor: (cb) => cb(props.isInstructor),
      classRoomId: (cb) => cb(props.classRoomId),
      ...pluginsConfigs.assignments,
    },
  };

  const bibliuConfig = isBibliuEnabled
    ? {
        bibliu: {
          ...pluginsConfigs.bibliu,
          ltiConsumerEndpoint: endpoints.BIBLIU_LTI_CONSUMER(),
          userId: user.objectId,
          sessionToken: user.sessionToken,
          classRoomId,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        },
      }
    : {};

  const talisConfig = isTalisEnabled
    ? {
        talis: {
          ...pluginsConfigs.talis,
          ltiConsumerEndpoint: endpoints.TALIS_LTI_CONSUMER(),
          userId: user.objectId,
          sessionToken: user.sessionToken,
          spaceId: classRoomId,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
          isInstructor: props.isInstructor,
        },
      }
    : {};

  const echoConfig = isEchoEnabled
    ? {
        echo: {
          ...pluginsConfigs.echo,
          ltiConsumerEndpoint: endpoints.ECHO_LTI_CONSUMER(),
          userId: user.objectId,
          sessionToken: user.sessionToken,
          spaceId: classRoomId,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
          isInstructor: props.isInstructor,
        },
      }
    : {};

  const planetConfig = isPlanetEnabled
    ? {
        planet: {
          ...pluginsConfigs.planet,
          ltiConsumerEndpoint: endpoints.PLANET_LTI_CONSUMER(),
          userId: user.objectId,
          sessionToken: user.sessionToken,
          spaceId: classRoomId,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
          isInstructor: props.isInstructor,
        },
      }
    : {};

  const panoptoConfig = isPanoptoEnabled
    ? {
        panopto: {
          ...pluginsConfigs.panopto,
          ltiConsumerEndpoint: endpoints.PANOPTO_LTI_CONSUMER(),
          userId: user.objectId,
          sessionToken: user.sessionToken,
          spaceId: classRoomId,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
          isInstructor: props.isInstructor,
        },
      }
    : {};

  return {
    ...pluginsConfigs,
    ...turnitinConfig,
    ...assignmentsConfig,
    ...bibliuConfig,
    ...talisConfig,
    ...echoConfig,
    ...planetConfig,
    ...panoptoConfig,
    handin: {
      ...pluginsConfigs.handin,
      sessionToken: user.sessionToken,
      userId: user.objectId,
    },
    link: {
      ...pluginsConfigs.link,
      scraperUrl: endpoints.AULA_WEB_SCAPER(),
      sessionToken: user.sessionToken,
    },
    gform: {
      ...pluginsConfigs.gform,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    gdoc: {
      ...pluginsConfigs.gdoc,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    gslides: {
      ...pluginsConfigs.gslides,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    gspreadsheet: {
      ...pluginsConfigs.gspreadsheet,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    oembed: {
      ...pluginsConfigs.oembed,
      proxyUrl: endpoints.OEMBED_PROXY(),
    },
    cloudapp: {
      ...pluginsConfigs.cloudapp,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    jumpshare: {
      ...pluginsConfigs.jumpshare,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    flatio: {
      ...pluginsConfigs.flatio,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    typeform: {
      ...pluginsConfigs.typeform,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    codepen: {
      ...pluginsConfigs.codepen,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    padlet: {
      ...pluginsConfigs.padlet,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    nearpod: {
      ...pluginsConfigs.nearpod,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    clypit: {
      ...pluginsConfigs.clypit,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    onedrive: {
      ...pluginsConfigs.onedrive,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    pinterest: {
      ...pluginsConfigs.pinterest,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    msform: {
      ...pluginsConfigs.pinterest,
      scraperUrl: endpoints.WEB_SCAPER(),
    },
    file: {
      ...pluginsConfigs.file,
      uploadUrl: endpoints.FILE_UPLOAD(),
      sessionToken: user.sessionToken,
      userId: user.objectId,
      dropOverlayPaddings: paddings,
    },
    bbb: {
      ...pluginsConfigs.bbb,
      ltiConsumerEndpoint: endpoints.BBB_LTI_CONSUMER(),
      userId: user.objectId,
      sessionToken: user.sessionToken,
      isInstructor: props.isInstructor,
      sessionId: props.classRoomId,
      spaceId: props.classRoomId,
      sessionContext: 'spaces',
      sessionDescription: `Join the session for the ${spaceName} space`,
      disableControl: props.type === 'inbox' || !isBBBEnabled,
      analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
    },
  };
};

const endpointsSelector = defaultMemoize((endpointsObj) => ({
  ASSIGNMENTS: endpointsObj.ASSIGNMENTS(),
  FILE_UPLOAD: endpointsObj.FILE_UPLOAD(),
  LTI_CONSUMER: endpointsObj.LTI_CONSUMER(),
  OEMBED_PROXY: endpointsObj.OEMBED_PROXY(),
  WEB_SCAPER: endpointsObj.WEB_SCAPER(),
}));

const assignmentsSelector = (state) => state.data.assignments;
const getAssignments = createSelector(
  [assignmentsSelector, currentClassRoomId],
  (assignments, currentClassRoom) =>
    getAssignmentsArray(assignments, currentClassRoom).filter(
      (assignment) => !!assignment.provider
    )
);

const mapStateToProps = (state) => ({
  accessToken: getAccessToken(state, state.classRoom.current),
  classRoomId: currentClassRoomId(state),
  spaceName:
    state.classRoom.current &&
    state.data.classRooms[state.classRoom.current].name,
  user: state.user.user,
  institution: state.institution.general.institution,
  isInstructor: isInstructor(state.user.userId),
  isTurnitinEnabled: getIsTurnItInEnabled(state),
  isBBBEnabled: bbbEnabled(state),
  isBibliuEnabled: getIsBibliuEnabled(state),
  isTalisEnabled: getIsTalisEnabled(state),
  isEchoEnabled: getIsEchoEnabled(state),
  isPlanetEnabled: getIsPlanetEnabled(state),
  isPanoptoEnabled: getIsPanoptoEnabled(state),
  assignments: getAssignments(state),
  isFetchingAssignments: state.assignments.isFetching,
  endpoints: endpointsSelector(endpoints),
});

const mapDispatchToProps = (_, ownProps) => ({
  trackEvent: (type, postIdObj) => () =>
    analyticsTrackEvent(editorTypeToAnalyticsEventType(type), {
      ...postIdObj,
      activeClassRoom: ownProps.classRoomId,
    }),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const props = {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
  };
  return {
    ...props,
    trackEvent:
      props.type === COMMENT
        ? props.trackEvent(props.type, { postId: props.postId })
        : props.trackEvent(props.type, {}),
    pluginsConfigs: mergePluginsConfigProps(props),
  };
};

const EditorPlusContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  { withRef: true }
)(EditorPlus);

export default EditorPlusContainer;
